import React from 'react';
import Box from '@components/Atoms/Box';
import Typography from '@components/Atoms/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useCurriculumSubjects, SelectedSubject } from './hooks';

interface SubjectSelectorProps {
  onSubjectSelect: (subject: SelectedSubject | null) => void;
  selectedSubject: SelectedSubject | null;
  districtId: number | undefined;
  curriculumId: number | undefined;
}

const SubjectSelector: React.FC<SubjectSelectorProps> = ({
  onSubjectSelect,
  selectedSubject,
  districtId,
  curriculumId,
}) => {
  
  const { data: subjects, isLoading: isLoadingSubjects } =
    useCurriculumSubjects(curriculumId);

  // Auto-select first subject when subjects load
  React.useEffect(() => {
    if (subjects && subjects.length > 0 && !selectedSubject && curriculumId) {
      onSubjectSelect({
        id: subjects[0].id,
        name: subjects[0].display_name,
        curriculumId: curriculumId,
      });
    }
  }, [subjects, selectedSubject, curriculumId, onSubjectSelect]);



  const handleSubjectChange = (event: SelectChangeEvent<number>) => {
    const subjectId = event.target.value as number;
    const subject = subjects?.find((s) => s.id === subjectId);
    if (subject && curriculumId) {
      onSubjectSelect({
        id: subject.id,
        name: subject.display_name,
        curriculumId: curriculumId,
      });
    }
  };

  if (!districtId || !curriculumId) {
    return (
      <Box sx={{ p: 2, mb: 3, bgcolor: 'warning.light', borderRadius: 1 }}>
        <Typography>
          No district or curriculum data available. Please ensure you have district admin access.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel id="subject-select-label">Subject</InputLabel>
          <Select
            labelId="subject-select-label"
            id="subject-select"
            value={selectedSubject?.id || ''}
            label="Subject"
            onChange={handleSubjectChange}
            disabled={isLoadingSubjects || !subjects?.length}
          >
            {isLoadingSubjects ? (
              <MenuItem value="">Loading...</MenuItem>
            ) : (
              subjects?.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.display_name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default SubjectSelector;
